import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  BooleanInput,
  FileInput,
  ImageField,
  ArrayInput,
  SimpleFormIterator,
  ReferenceArrayInput,
  SelectArrayInput,
  useNotify,
  useRedirect,
  useUpdate,
  Toolbar,
  SaveButton,
  Edit,
  required
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
import { useParams } from 'react-router-dom';

import { htmlToBlocks, blocksToHtml } from '../../../helpers/htmlUtils';
import { uploadImage } from '../../../config/services/strapi/strapi';

function SolucionEnergeticaEdit(props: any) {
  const { id } = useParams();

  const [imagesChanged, setImagesChanged] = useState({
    portada: false,
    banner: false,
    icono: false,
    imagen_industria: false,
    imagen_proyectos: false,
    imagen_beneficios: false,
    caracteristicas: false,
  });

  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();

  const handleSubmit = async(data: any) => {
    const token = localStorage.getItem('strapi_jwt');
    if (!token) {
      notify('No se encontró el token de autenticación', { type: 'error' });
      return;
    }

    try {
      const payload = {
        ...data,
        descripcion: typeof data.descripcion === 'string' ? htmlToBlocks(data.descripcion) : data.descripcion,
        beneficios: typeof data.beneficios === 'string' ? htmlToBlocks(data.beneficios) : data.beneficios,
        caracteristicas: await Promise.all(data.caracteristicas.map(async (block: any) => {
          if (block.imagen && block.imagen.rawFile) {
            const imageResult = await uploadImage(block.imagen.rawFile, token);
            return {
              ...block,
              contenido: typeof block.contenido === 'string' ? htmlToBlocks(block.contenido) : block.contenido,
              imagen: imageResult
            };
          }

          return {
            ...block,
            contenido: typeof block.contenido === 'string' ? htmlToBlocks(block.contenido) : block.contenido
          };
        })),
      };

      if (imagesChanged.portada && data.portada?.rawFile) {
        const portadaResult = await uploadImage(data.portada.rawFile, token);
        payload.portada = portadaResult;
      }

      if (imagesChanged.banner && data.banner?.rawFile) {
        const bannerResult = await uploadImage(data.banner.rawFile, token);
        payload.banner = bannerResult;
      }

      if (imagesChanged.icono && data.icono?.rawFile) {
        const iconoResult = await uploadImage(data.icono.rawFile, token);
        payload.icono = iconoResult;
      }

      if (imagesChanged.imagen_industria && data.imagen_industria?.rawFile) {
        const imagenIndustriaResult = await uploadImage(data.imagen_industria.rawFile, token);
        payload.imagen_industria = imagenIndustriaResult;
      }

      if (imagesChanged.imagen_proyectos && data.imagen_proyectos?.rawFile) {
        const imagenProyectosResult = await uploadImage(data.imagen_proyectos.rawFile, token);
        payload.imagen_proyectos = imagenProyectosResult;
      }

      if (imagesChanged.imagen_beneficios && data.imagen_beneficios?.rawFile) {
        const imagenBeneficiosResult = await uploadImage(data.imagen_beneficios.rawFile, token);
        payload.imagen_beneficios = imagenBeneficiosResult;
      }

      delete payload.portada.documentId;

      if (payload.banner) {
        delete payload.banner.documentId;
      }
      if (payload.icono) {
        delete payload.icono.documentId;
      }
      if (payload.imagen_industria) {
        delete payload.imagen_industria.documentId;
      }
      if (payload.imagen_proyectos) {
        delete payload.imagen_proyectos.documentId;
      }
      if (payload.imagen_beneficios) {
        delete payload.imagen_beneficios.documentId;
      }
      
      delete payload.id; // Asegurarse de no enviar el ID en el payload
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.publishedAt;
      delete payload.documentId;
      delete payload.ref;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.publishedAt;

      update(
        'solucion-energeticas',
        { data: payload, id: id },
        {
          onSuccess: () => {
            notify('Elemento actualizado');
            redirect('list', 'solucion-energeticas');
          },
          onError: (error: any) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          },
        }
      );
    }
    catch(error) {
      console.error('Error al actualizar la solución energética:', error);
      notify('Error al actualizar la solución energética', { type: 'error' });
      return;
    }
  };

  useEffect(() => {
    document.title = 'Editar Solución Energética';
  }, []);

  return (
    <Edit {...props}>
      <SimpleForm onSubmit={handleSubmit} toolbar={<Toolbar><SaveButton alwaysEnable /></Toolbar>}>
        <TextInput source="id" disabled />
        <TextInput source="nombre" required />
        <FileInput
          source='portada'
          label='Imagen de Portada'
          onChange={() => setImagesChanged(prev => ({ ...prev, portada: true }))}
        >
          <>
            {imagesChanged.portada && <ImageField source="src" />}
            {!imagesChanged.portada && <ImageField source="url" />}
          </>
        </FileInput>
        <FileInput
          source='banner'
          label='Imagen de Banner'
          onChange={() => setImagesChanged(prev => ({ ...prev, banner: true }))}
        >
          <>
            {imagesChanged.banner && <ImageField source="src" />}
            {!imagesChanged.banner && <ImageField source="url" />}
          </>
        </FileInput>
        <FileInput
          source='icono'
          label='Imagen de Icono'
          onChange={() => setImagesChanged(prev => ({ ...prev, icono: true }))}
        >
          <>
            {imagesChanged.icono && <ImageField source="src" />}
            {!imagesChanged.icono && <ImageField source="url" />}
          </>
        </FileInput>
        <RichTextInput
          source="descripcion"
          format={blocksToHtml}
          parse={htmlToBlocks}
        />
        <ArrayInput source="caracteristicas" label="Características">
          <SimpleFormIterator>
            <FileInput
              source="imagen"
              label="Imagen de Característica"
              className='mt-4'
              onChange={() => setImagesChanged(prev => ({ ...prev, caracteristicas: true }))}
              validate={[required()]}
            >
              <>
                {imagesChanged.caracteristicas && <ImageField source="src" />}
                {!imagesChanged.caracteristicas && <ImageField source="url" />}
              </>
            </FileInput>
            <RichTextInput
              source="contenido"
              label="Contenido Enriquecido"
              format={blocksToHtml}
              parse={htmlToBlocks}
              validate={[required()]}
            />
          </SimpleFormIterator>
        </ArrayInput>
        <RichTextInput
          source="beneficios"
          label="Beneficios"
          format={blocksToHtml}
          parse={htmlToBlocks}
          isRequired
        />
        <FileInput
          source='imagen_industria'
          label='Imagen de la Industria'
          onChange={() => setImagesChanged(prev => ({ ...prev, imagen_industria: true }))}
        >
          <>
            {imagesChanged.imagen_industria && <ImageField source="src" />}
            {!imagesChanged.imagen_industria && <ImageField source="url" />}
          </>
        </FileInput>
        <FileInput
          source='imagen_proyectos'
          label='Imagen de Proyectos'
          onChange={() => setImagesChanged(prev => ({ ...prev, imagen_proyectos: true }))}
        >
          <>
            {imagesChanged.imagen_proyectos && <ImageField source="src" />}
            {!imagesChanged.imagen_proyectos && <ImageField source="url" />}
          </>
        </FileInput>
        <FileInput
          source='imagen_beneficios'
          label='Imagen de Beneficios'
          onChange={() => setImagesChanged(prev => ({ ...prev, imagen_beneficios: true }))}
        >
          <>
            {imagesChanged.imagen_beneficios && <ImageField source="src" />}
            {!imagesChanged.imagen_beneficios && <ImageField source="url" />}
          </>
        </FileInput>
        <TextInput source="url" required />
        <BooleanInput source="externo" />
        <BooleanInput source="visible" />
        <ReferenceArrayInput source='industrias' reference='industries' label='Industrias'>
          <SelectArrayInput optionText="nombre" />
        </ReferenceArrayInput>
      </SimpleForm>
    </Edit>
  );
}

export default SolucionEnergeticaEdit